import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  UsersIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  KeyIcon,
  EnvelopeIcon,
  PhoneIcon,
  XMarkIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowDownTrayIcon,
  DocumentChartBarIcon
} from '@heroicons/react/24/outline'
import { RootState } from '../../store'
import DashboardCard from '../../components/DashboardCard'
import toast from 'react-hot-toast'
import api from '../../services/api'
import AdminQuestionsTab from './components/AdminQuestionsTab'
import * as XLSX from 'xlsx'

interface Recruiter {
  id: number
  email: string
  first_name: string
  last_name: string
  phone_number?: string
  created_at?: string
  is_active: boolean
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)

  const [activeTab, setActiveTab] = useState<'recruiters' | 'questions'>('recruiters')
  const [recruiters, setRecruiters] = useState<Recruiter[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedRecruiter, setSelectedRecruiter] = useState<Recruiter | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  // Form states
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone_number: ''
  })
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    loadRecruiters()
  }, [])

  const loadRecruiters = async () => {
    try {
      setIsLoading(true)
      const response = await api.get('/admin/recruiters')
      setRecruiters(response.data.recruiters || [])
    } catch (error) {
      console.error('Failed to load recruiters:', error)
      toast.error('Failed to load recruiters')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateRecruiter = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate email format
    if (!formData.email.endsWith('@cyfuture.com')) {
      toast.error('Recruiter email must end with @cyfuture.com')
      return
    }

    try {
      await api.post('/admin/recruiters', formData)
      toast.success('Recruiter created successfully!')
      setShowCreateModal(false)
      resetForm()
      loadRecruiters()
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to create recruiter'
      toast.error(errorMessage)
    }
  }

  const handleUpdateRecruiter = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedRecruiter) return

    try {
      const updateData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone_number: formData.phone_number,
        ...(formData.password && { password: formData.password })
      }

      await api.put(`/admin/recruiters/${selectedRecruiter.id}`, updateData)
      toast.success('Recruiter updated successfully!')
      setShowEditModal(false)
      resetForm()
      setSelectedRecruiter(null)
      loadRecruiters()
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to update recruiter'
      toast.error(errorMessage)
    }
  }

  const handleDeleteRecruiter = async (recruiterId: number, recruiterEmail: string) => {
    if (!window.confirm(`Are you sure you want to delete recruiter "${recruiterEmail}"? This action cannot be undone.`)) {
      return
    }

    try {
      await api.delete(`/admin/recruiters/${recruiterId}`)
      toast.success('Recruiter deleted successfully!')
      loadRecruiters()
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete recruiter'
      toast.error(errorMessage)
    }
  }

  const openEditModal = (recruiter: Recruiter) => {
    setSelectedRecruiter(recruiter)
    setFormData({
      email: recruiter.email,
      password: '',
      first_name: recruiter.first_name,
      last_name: recruiter.last_name,
      phone_number: recruiter.phone_number || ''
    })
    setShowEditModal(true)
  }

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      phone_number: ''
    })
    setShowPassword(false)
  }

  const handleExportCandidateData = async () => {
    try {
      const response = await api.get('/admin/candidates/export')
      const candidates = response.data?.candidates || []

      if (candidates.length === 0) {
        toast.error('No candidates to export')
        return
      }

      const exportData = candidates.map((c: { name: string; email: string; college: string; phone: string }, index: number) => ({
        'S.No': index + 1,
        'Name': c.name,
        'Email': c.email,
        'College': c.college,
        'Phone': c.phone
      }))

      const ws = XLSX.utils.json_to_sheet(exportData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Candidates')

      const maxWidth = 50
      const colWidths = Object.keys(exportData[0] || {}).map(key => ({
        wch: Math.min(maxWidth, Math.max(key.length, ...exportData.map((row: Record<string, unknown>) => String(row[key] || '').length)))
      }))
      ws['!cols'] = colWidths

      const fileName = `Candidate_Data_${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`
      XLSX.writeFile(wb, fileName)

      toast.success(`Exported ${candidates.length} candidate(s) successfully!`)
    } catch (error) {
      console.error('Error exporting candidate data:', error)
      toast.error('Failed to export candidate data')
    }
  }

  const handleExportRecruiters = () => {
    try {
      if (filteredRecruiters.length === 0) {
        toast.error('No recruiters to export')
        return
      }

      // Prepare data for Excel export
      const exportData = filteredRecruiters.map((recruiter, index) => ({
        'S.No': index + 1,
        'Recruiter ID': recruiter.id,
        'Email': recruiter.email,
        'First Name': recruiter.first_name,
        'Last Name': recruiter.last_name,
        'Full Name': `${recruiter.first_name} ${recruiter.last_name}`,
        'Phone Number': recruiter.phone_number || 'N/A',
        'Status': recruiter.is_active ? 'Active' : 'Inactive',
        'Created At': recruiter.created_at ? new Date(recruiter.created_at).toLocaleDateString() : 'N/A'
      }))

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(exportData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Recruiters')

      // Auto-size columns
      const maxWidth = 50
      const colWidths = Object.keys(exportData[0] || {}).map(key => ({
        wch: Math.min(
          maxWidth,
          Math.max(
            key.length,
            ...exportData.map(row => String(row[key as keyof typeof row] || '').length)
          )
        )
      }))
      ws['!cols'] = colWidths

      // Generate filename with date
      const fileName = `Recruiters_${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`

      // Download file
      XLSX.writeFile(wb, fileName)

      toast.success(`Exported ${filteredRecruiters.length} recruiter(s) successfully!`)
    } catch (error) {
      console.error('Error exporting recruiters:', error)
      toast.error('Failed to export recruiters')
    }
  }

  const filteredRecruiters = recruiters.filter(recruiter =>
    recruiter.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    recruiter.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    recruiter.last_name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Admin Dashboard
              </h1>
              <p className="text-gray-600 mt-2">
                Manage system access and database configurations
              </p>
            </div>
            {activeTab === 'recruiters' && (
              <div className="flex items-center gap-3">
                <button
                  onClick={handleExportCandidateData}
                  className="btn btn-outline flex items-center"
                >
                  <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                  Export Candidate Data
                </button>
                <button
                  onClick={() => navigate('/admin/report')}
                  className="btn btn-outline flex items-center border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                >
                  <DocumentChartBarIcon className="w-5 h-5 mr-2" />
                  Report
                </button>
                <button
                  onClick={() => {
                    resetForm()
                    setShowCreateModal(true)
                  }}
                  className="btn btn-primary flex items-center"
                >
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Create Recruiter
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('recruiters')}
              className={`${activeTab === 'recruiters'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              Recruiter Management
            </button>
            <button
              onClick={() => setActiveTab('questions')}
              className={`${activeTab === 'questions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              Question Management
            </button>
          </nav>
        </div>

        {/* Tab Content: Question Management */}
        {activeTab === 'questions' && (
          <AdminQuestionsTab />
        )}

        {/* Tab Content: Recruiter Management */}
        {activeTab === 'recruiters' && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <DashboardCard
                title="Total Recruiters"
                value={recruiters.length.toString()}
                icon={UsersIcon}
                iconColor="text-blue-600"
                bgColor="bg-blue-50"
              />
              <DashboardCard
                title="Active Recruiters"
                value={recruiters.filter(r => r.is_active).length.toString()}
                icon={UsersIcon}
                iconColor="text-green-600"
                bgColor="bg-green-50"
              />
              <DashboardCard
                title="Cyfuture Emails"
                value={recruiters.filter(r => r.email.endsWith('@cyfuture.com')).length.toString()}
                icon={EnvelopeIcon}
                iconColor="text-purple-600"
                bgColor="bg-purple-50"
              />
            </div>

            {/* Recruiters List */}
            <div className="card p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Recruiters
                </h2>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search recruiters..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                  <button
                    onClick={handleExportRecruiters}
                    className="flex items-center px-4 py-2 bg-green-50 text-green-700 border border-green-200 rounded-md hover:bg-green-100 transition-colors font-medium text-sm"
                    title="Export recruiters to Excel"
                  >
                    <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                    Export
                  </button>
                </div>
              </div>

              {filteredRecruiters.length === 0 ? (
                <div className="text-center py-12">
                  <UsersIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">
                    {searchQuery ? 'No recruiters found matching your search' : 'No recruiters created yet'}
                  </p>
                  {!searchQuery && (
                    <button
                      onClick={() => {
                        resetForm()
                        setShowCreateModal(true)
                      }}
                      className="btn btn-primary mt-4"
                    >
                      Create Your First Recruiter
                    </button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Recruiter
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Phone
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredRecruiters.map((recruiter) => (
                        <tr key={recruiter.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <span className="text-blue-600 font-semibold">
                                  {recruiter.first_name[0]}{recruiter.last_name[0]}
                                </span>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {recruiter.first_name} {recruiter.last_name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  ID: {recruiter.id}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900">
                              <EnvelopeIcon className="w-4 h-4 mr-2 text-gray-400" />
                              {recruiter.email}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {recruiter.phone_number || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${recruiter.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                              }`}>
                              {recruiter.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => openEditModal(recruiter)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Edit recruiter"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteRecruiter(recruiter.id, recruiter.email)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete recruiter"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Create Recruiter Modal */}
            {showCreateModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-semibold text-gray-900">
                      Create New Recruiter
                    </h3>
                    <button
                      onClick={() => {
                        setShowCreateModal(false)
                        resetForm()
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="w-6 h-6" />
                    </button>
                  </div>

                  <form onSubmit={handleCreateRecruiter} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="name@cyfuture.com"
                        className="input w-full"
                      />
                      <p className="mt-1 text-xs text-gray-500">Must end with @cyfuture.com</p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.first_name}
                          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                          className="input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.last_name}
                          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                          className="input w-full"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={formData.phone_number}
                        onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                        className="input w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Password <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          required
                          minLength={8}
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          className="input w-full pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          {showPassword ? (
                            <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                          ) : (
                            <EyeIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">Minimum 8 characters</p>
                    </div>

                    <div className="flex space-x-3 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowCreateModal(false)
                          resetForm()
                        }}
                        className="btn btn-outline flex-1"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary flex-1"
                      >
                        Create Recruiter
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Edit Recruiter Modal */}
            {showEditModal && selectedRecruiter && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-semibold text-gray-900">
                      Edit Recruiter
                    </h3>
                    <button
                      onClick={() => {
                        setShowEditModal(false)
                        setSelectedRecruiter(null)
                        resetForm()
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="w-6 h-6" />
                    </button>
                  </div>

                  <form onSubmit={handleUpdateRecruiter} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        disabled
                        className="input w-full bg-gray-100 cursor-not-allowed"
                      />
                      <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.first_name}
                          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                          className="input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.last_name}
                          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                          className="input w-full"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={formData.phone_number}
                        onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                        className="input w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          minLength={8}
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          placeholder="Leave blank to keep current password"
                          className="input w-full pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          {showPassword ? (
                            <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                          ) : (
                            <EyeIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">Minimum 8 characters (if changing)</p>
                    </div>

                    <div className="flex space-x-3 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowEditModal(false)
                          setSelectedRecruiter(null)
                          resetForm()
                        }}
                        className="btn btn-outline flex-1"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary flex-1"
                      >
                        Update Recruiter
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default AdminDashboard
